/**
 * BillingPeriodCard — native rewrite of the legacy BillingPeriod row. Presentational:
 * reads a `BillingPeriodVM` (rich working state with a STABLE local id — C6) and emits
 * partial updates to its parent heater card. No store/context coupling.
 */

import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { toNumber } from 'utils/formatting/numberUtils'
import { GRAY_400, GRAY_900 } from 'constants/styling/theme'
import { HeaterConsumptionUnit, HotWaterGenerationType } from 'constants/purchaseFlow/consumptionCertificateTypes'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIDatePicker } from 'components/common/MUIDatePicker'
import { MUIFloatNumberField } from 'components/common/MUIFloatNumberField'
import { MUINumberField } from 'components/common/MUINumberField'

import type { BillingPeriodVM } from '../consumptionCertificate.codec'
import { usePeriodPickerBounds } from './usePeriodPickerBounds'
import { MUIIconButton } from 'components/common/MUIIconButton'

export interface BillingPeriodCardProps {
  billing: BillingPeriodVM
  index: number
  hotWaterType?: HotWaterGenerationType
  unit?: HeaterConsumptionUnit
  showErrors: boolean
  onInputBlur: () => void
  onUpdate: (partial: Partial<BillingPeriodVM>) => void
  onRemove: () => void
}

export const BillingPeriodCard: React.FC<BillingPeriodCardProps> = ({
  billing,
  index,
  hotWaterType,
  unit,
  showErrors,
  onInputBlur,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation(['heating_system', 'common'])

  const isValidConsumption = useMemo(
    () => billing.consumption !== null && billing.consumption !== undefined && toNumber(billing.consumption) >= 0,
    [billing.consumption]
  )

  const isValidHotWaterConsumption = useMemo(
    () =>
      hotWaterType === HotWaterGenerationType.KNOW_HOT_WATER_CONSUMPTION_IN_KWH &&
      billing.hotWaterConsumptionKwh !== null &&
      billing.hotWaterConsumptionKwh !== undefined &&
      billing.hotWaterConsumptionKwh > 0,
    [billing.hotWaterConsumptionKwh, hotWaterType]
  )

  const { startMinDate, startMaxDate, endMinDate, endMaxDate, isRangeInvalid } = usePeriodPickerBounds(billing.startDate, billing.endDate)

  return (
    <BorderBoxWrapper padding={2} borderColor={GRAY_400} gap={2} display="flex" flexDirection="column">

      <Stack direction="row" justifyContent="space-between" alignItems="center">

        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('billing_period')} {index + 1}
        </Typography>

        {index > 2 &&
          <MUIIconButton variant="plain" size="lg" icon={<CloseRoundedIcon />} ariaLabel="delete-billing-period" onClick={onRemove} />
        }

      </Stack>

      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">

        <MUIDatePicker
          required
          value={billing.startDate}
          label={t('beginning')}
          views={['year', 'month', 'day']}
          format="DD/MM/YYYY"
          minDate={startMinDate}
          maxDate={startMaxDate}
          isError={(showErrors && !billing.startDate) || isRangeInvalid}
          onInputBlur={onInputBlur}
          onChange={(newValue) => onUpdate({ startDate: newValue })}
        />

        <Typography variant="text-md" color={GRAY_900} marginTop={2}>
          {t('common:to')}
        </Typography>

        <MUIDatePicker
          required
          value={billing.endDate}
          label={t('end')}
          views={['year', 'month', 'day']}
          format="DD/MM/YYYY"
          minDate={endMinDate}
          maxDate={endMaxDate}
          isError={(showErrors && !billing.endDate) || isRangeInvalid}
          onInputBlur={onInputBlur}
          onChange={(newValue) => onUpdate({ endDate: newValue })}
        />

      </Stack>

      <Box width={{ xs: '80%', lg: '45%' }}>
        <MUIFloatNumberField
          required
          placeholder={unit ? t(`unit_capture_type.${unit}`) : ''}
          inputBoxWidth="100%"
          label={t('consumption')}
          value={billing.consumption}
          isError={showErrors && !isValidConsumption}
          onBlur={onInputBlur}
          onChange={(value) => onUpdate({ consumption: value })}
        />
      </Box>

      {hotWaterType === HotWaterGenerationType.KNOW_HOT_WATER_CONSUMPTION_IN_KWH &&
        <Box width={{ xs: '80%', lg: '45%' }}>
          <MUINumberField
            min={0}
            required
            suffix={t('unit_capture_type.KWH')}
            inputBoxWidth="100%"
            label={t('hot_water_consumption')}
            value={billing.hotWaterConsumptionKwh}
            isError={showErrors && !isValidHotWaterConsumption}
            onBlur={onInputBlur}
            onChange={(value) => onUpdate({ hotWaterConsumptionKwh: value })}
          />
        </Box>
      }

    </BorderBoxWrapper>
  )
}
